/* Demo-data detection + attribution sanitising.

   The backend tags synthetic dashboard sections with `"demo": true` markers
   (top-level, per-section, or per-item depending on shape). Panels showing
   such data render a SIMULATED badge so fabricated content is never mistaken
   for live intelligence. We also scrub fake model/outlet attributions
   ("gpt-4o", "claude-sonnet", "Lloyd's List") that no real pipeline produced. */

interface DemoMarkers {
  demo?: boolean
  demo_sections?: string[]
}

type SectionValue =
  | { demo?: boolean }
  | Array<{ demo?: boolean } | null | undefined>
  | null
  | undefined

/**
 * True when a dashboard section should be labelled as demo/simulated data.
 * Accepts any of the marker shapes the backend may emit:
 *  - payload-level `demo: true` (whole dashboard is demo)
 *  - payload-level `demo_sections: ["ai_forecasts", ...]`
 *  - section object with `demo: true`
 *  - array section where any item carries `demo: true`
 */
export function sectionIsDemo(
  payload: DemoMarkers | undefined | null,
  sectionKey: string,
  section: SectionValue,
): boolean {
  if (payload) {
    if (payload.demo === true) return true
    if (Array.isArray(payload.demo_sections) && payload.demo_sections.includes(sectionKey)) {
      return true
    }
  }
  if (Array.isArray(section)) {
    return section.some((item) => item != null && item.demo === true)
  }
  if (section && typeof section === "object") return section.demo === true
  return false
}

/* Attributions that imply a model/outlet this product never called/licensed.
   Matched case-insensitively as substrings. */
const FAKE_ATTRIBUTIONS = [
  "gpt-4o",
  "gpt-4",
  "claude-sonnet",
  "claude-opus",
  "claude",
  "openai",
  "lloyd's list",
  "lloyds list",
]

export function isFakeAttribution(source: string | null | undefined): boolean {
  if (!source) return false
  const s = source.toLowerCase()
  return FAKE_ATTRIBUTIONS.some((fake) => s.includes(fake))
}

/**
 * Display-safe source label: fabricated attributions are replaced with an
 * honest fallback ("Simulated forecast" by default).
 */
export function displaySource(
  source: string | null | undefined,
  fallback = "Simulated forecast",
): string {
  if (!source || isFakeAttribution(source)) return fallback
  return source
}
